// App-wide theme state: a color MODE (light | dark | system) and a color
// PALETTE (one of 16 seed-color schemes). Both persist to localStorage and
// apply to <html data-theme data-palette>. The pre-paint inline script in
// index.html applies the stored values before the app boots (no flash);
// this module owns runtime changes and keeps a reactive copy for the UI.
//
// Mirrors the tb-auth (login.telebroad.com) theme switcher: a mode toggle
// plus a 16-swatch palette grid.

const MODE_KEY = 'hook_theme';     // 'light' | 'dark' | 'system'
const PALETTE_KEY = 'hook_palette'; // palette name (see PALETTES)

// The 16 palettes. `swatch` is the seed color (the picker dot). The FULL M3
// scheme for each (every role, light + dark) is generated from that seed into
// palettes.generated.css by scripts/gen-themes.mjs -- run `npm run gen:themes`
// after changing a seed here so the two stay in sync.
export const PALETTES = [
  { name: 'blue',    label: 'Blue',    swatch: '#0d63c3' },
  { name: 'indigo',  label: 'Indigo',  swatch: '#3f51b5' },
  { name: 'violet',  label: 'Violet',  swatch: '#6a4fc4' },
  { name: 'purple',  label: 'Purple',  swatch: '#8e3fb0' },
  { name: 'pink',    label: 'Pink',    swatch: '#b5298a' },
  { name: 'rose',    label: 'Rose',    swatch: '#c02b52' },
  { name: 'red',     label: 'Red',     swatch: '#c0392b' },
  { name: 'orange',  label: 'Orange',  swatch: '#b85c00' },
  { name: 'amber',   label: 'Amber',   swatch: '#9a6700' },
  { name: 'lime',    label: 'Lime',    swatch: '#5b7d00' },
  { name: 'green',   label: 'Green',   swatch: '#2e7d32' },
  { name: 'emerald', label: 'Emerald', swatch: '#0f7a5a' },
  { name: 'teal',    label: 'Teal',    swatch: '#00796b' },
  { name: 'cyan',    label: 'Cyan',    swatch: '#00779c' },
  { name: 'slate',   label: 'Slate',   swatch: '#4a5b6b' },
  { name: 'brown',   label: 'Brown',   swatch: '#7a5440' },
];

const MODES = ['light', 'dark', 'system'];
const VALID_PALETTES = new Set(PALETTES.map((p) => p.name));

function readMode() {
  const m = localStorage.getItem(MODE_KEY);
  return MODES.includes(m) ? m : 'system';
}
function readPalette() {
  const p = localStorage.getItem(PALETTE_KEY);
  return p && VALID_PALETTES.has(p) ? p : 'blue';
}

function systemPrefersDark() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

class Theme {
  mode = $state(typeof localStorage !== 'undefined' ? readMode() : 'system');
  palette = $state(typeof localStorage !== 'undefined' ? readPalette() : 'blue');
  // Effective light/dark after resolving "system".
  resolved = $state('light');

  constructor() {
    if (typeof window === 'undefined') return;
    this.apply();
    // When in system mode, track OS preference changes live.
    this._mq = window.matchMedia('(prefers-color-scheme: dark)');
    this._onChange = () => { if (this.mode === 'system') this.apply(); };
    this._mq.addEventListener('change', this._onChange);
  }

  apply() {
    const dark = this.mode === 'dark' || (this.mode === 'system' && systemPrefersDark());
    this.resolved = dark ? 'dark' : 'light';
    document.documentElement.dataset.theme = this.resolved;
    document.documentElement.dataset.palette = this.palette;
  }

  setMode(mode) {
    if (!MODES.includes(mode)) return;
    this.mode = mode;
    localStorage.setItem(MODE_KEY, mode);
    this.apply();
  }

  setPalette(name) {
    if (!VALID_PALETTES.has(name)) return;
    this.palette = name;
    localStorage.setItem(PALETTE_KEY, name);
    this.apply();
  }
}

export const theme = new Theme();
